import React from 'react';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import User from 'types/User';
import { MembershipTier } from 'types/SignUpData';
import TierChip, { TIER_CONFIG, TIER_OPTIONS } from 'components/project/TierChip';
import UserHistoryPanel from 'components/auth/UserHistoryPanel';
import { orcidToUrl, normalizeOrcidId } from 'utils/tierMapping';
import { UnifiedRequest } from 'types/ManageUserMemberships';
import { formatDate } from 'utils/manageMemberships';

interface ReviewRequestDialogProps {
  open: boolean;
  row: UnifiedRequest | null;
  tier: MembershipTier;
  comment: string;
  savingEmail: string | null;
  currentUser: User | null;
  onClose: () => void;
  onTierChange: (tier: MembershipTier) => void;
  onCommentChange: (comment: string) => void;
  onApprove: () => void;
  onDeny: () => void;
}

const ReviewRequestDialog: React.FC<ReviewRequestDialogProps> = ({
  open,
  row,
  tier,
  comment,
  savingEmail,
  currentUser,
  onClose,
  onTierChange,
  onCommentChange,
  onApprove,
  onDeny,
}) => {
  const isSaving = savingEmail === row?.email;
  const orcidRaw = row?.user.orcidId;
  const orcidDisplay = normalizeOrcidId(orcidRaw);
  const orcidHref = orcidToUrl(orcidRaw);

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {row?.requestType === 'new-user'
          ? 'Review New User Request'
          : 'Review Upgrade Request'}
      </DialogTitle>
      <DialogContent sx={{ position: 'relative' }}>
        {isSaving && (
          <Backdrop
            open
            sx={{
              position: 'absolute',
              zIndex: 1,
              bgcolor: 'rgba(255,255,255,0.7)',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Saving…
            </Typography>
          </Backdrop>
        )}
        {row && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* User info */}
            <Box sx={{ wordBreak: 'break-word' }}>
              <Typography variant="body2" color="text.secondary">
                User
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {row.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {row.email}
              </Typography>
              {row.affiliation && (
                <Typography variant="body2" color="text.secondary">
                  {row.affiliation}
                </Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="body2" color="text.secondary">
                ORCID iD
              </Typography>
              <Typography variant="body2">
                {orcidDisplay ? (
                  <Link href={orcidHref} target="_blank" rel="noopener noreferrer">
                    {orcidDisplay}
                  </Link>
                ) : (
                  'Not provided'
                )}
              </Typography>
            </Box>

            <Divider />

            {row.requestType === 'upgrade' ? (
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Current Tier
                  </Typography>
                  <TierChip tier={row.currentTier} />
                </Box>
                <ArrowUpwardIcon sx={{ color: 'info.main' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Requested Tier
                  </Typography>
                  <TierChip tier={row.requestedTier} />
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Membership Tier
                </Typography>
                <TierChip tier={row.currentTier} />
              </Box>
            )}

            <Divider />
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Justification
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  bgcolor: 'grey.50',
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {row.justification || 'No justification provided.'}
              </Typography>
            </Box>

            {row.requestDate && (
              <Typography variant="caption" color="text.secondary">
                Requested on {formatDate(row.requestDate)}
              </Typography>
            )}

            <Divider />
            <FormControl>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Assign Tier
              </Typography>
              <RadioGroup
                row
                value={tier}
                onChange={(e) => onTierChange(e.target.value as MembershipTier)}
              >
                {TIER_OPTIONS.map((t) => (
                  <FormControlLabel
                    key={t}
                    value={t}
                    control={<Radio size="small" />}
                    label={TIER_CONFIG[t].label}
                  />
                ))}
              </RadioGroup>
              <Typography variant="caption" color="text.secondary">
                {row.requestType === 'upgrade'
                  ? "Choose the tier to grant. Defaults to the user's requested tier."
                  : "Choose the tier to grant. Defaults to the tier the user selected at sign-up."}
              </Typography>
            </FormControl>

            <Divider />

            <TextField
              label="Admin Comment (optional)"
              placeholder="Add a comment about your decision..."
              value={comment}
              onChange={(e) => onCommentChange(e.target.value)}
              multiline
              rows={2}
              fullWidth
              helperText="This comment will be recorded with your decision in the user's history."
            />

            <UserHistoryPanel email={row.email} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        {currentUser && row?.requestType === 'new-user' && (
          <Button
            variant="outlined"
            color="primary"
            disabled={isSaving}
            component="a"
            href={`mailto:${row.email}?subject=Regarding your MSD-LIVE account&body=${encodeURIComponent(
              `Hi ${row.user.firstName},\nYou recently requested a user account on MSD-LIVE. Please provide a more detailed justification as to why you need access to MSD-LIVE.\nBest,\n${(currentUser.firstName || '').trim()}`.trim()
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact
          </Button>
        )}
        {currentUser && row?.requestType === 'upgrade' && (
          <Button
            variant="outlined"
            color="primary"
            disabled={isSaving}
            component="a"
            href={`mailto:${row.email}?subject=Regarding your MSD-LIVE membership tier upgrade request&body=${encodeURIComponent(
              `Hi ${row.user.firstName},\nYou recently requested a membership tier upgrade on MSD-LIVE. Please provide a more detailed justification as to why you need access to the higher tier.\nBest,\n${(currentUser.firstName || '').trim()}`.trim()
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact
          </Button>
        )}
        <Button variant="outlined" color="error" onClick={onDeny} disabled={isSaving}>
          Deny
        </Button>
        <Button variant="contained" color="success" onClick={onApprove} disabled={isSaving}>
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewRequestDialog;